(() => {
  if (window.ACPreload) return;

  const home = document.body.classList.contains('ac-reference--home');
  const events = new AbortController();
  const on = (target, type, listener, options = {}) => target.addEventListener(type, listener, { ...options, signal: events.signal });
  const jobs = [];
  const prepared = new WeakSet();
  const observed = new WeakSet();
  const dwellTimers = new Map();
  const rules = new Map();
  const supportsPrefetch = HTMLScriptElement.supports?.('speculationrules') && !window.Shopify?.designMode;
  let started = false;
  let active = 0;
  let startTimer;
  let hoverTimer;

  // Network requests stay native: no response cache, Blob URLs or extra decoding.
  const waitFor = (element, type, start) => new Promise((resolve) => {
    const finish = () => {
      clearTimeout(timeout);
      element.removeEventListener(type, finish);
      element.removeEventListener('error', finish);
      resolve();
    };
    const timeout = setTimeout(finish, 10000);
    element.addEventListener(type, finish, { once: true });
    element.addEventListener('error', finish, { once: true });
    start();
    if (type === 'load' ? element.complete : element.readyState >= 4) finish();
  });
  const pump = () => {
    if (!started || document.hidden || events.signal.aborted) return;
    while (active < 2 && jobs.length) {
      const job = jobs.shift();
      active += 1;
      Promise.resolve().then(job).catch(() => {}).finally(() => { active -= 1; pump(); });
    }
  };
  const enqueue = (element, job) => {
    if (prepared.has(element)) return;
    prepared.add(element);
    jobs.push(job);
  };
  const prepareImage = (source) => {
    if (!source.getAttribute('src') && !source.getAttribute('srcset')) return Promise.resolve();
    if (source.isConnected && source.complete && source.naturalWidth) return Promise.resolve();
    const image = source.isConnected ? source : new Image();
    return waitFor(image, 'load', () => {
      image.fetchPriority = 'low';
      image.decoding = 'async';
      // auto sizing only applies to lazy images; keep the shared layout fallback.
      image.sizes = source.sizes.replace(/^auto,\s*/, '');
      image.loading = 'eager';
      if (image !== source) {
        if (source.srcset) image.srcset = source.srcset;
        if (source.getAttribute('src')) image.src = source.src;
      }
    });
  };
  const within = (scope, selector) => {
    const elements = [...scope.querySelectorAll(selector)];
    if (scope instanceof Element && scope.matches(selector)) elements.unshift(scope);
    return elements;
  };
  const warm = (scope) => {
    // Follow content order so an early video need not wait for every page image.
    for (const element of within(scope, 'img, template, [data-ac-media-video]')) {
      if (element instanceof HTMLImageElement) {
        if (!element.isConnected || element.loading === 'lazy') enqueue(element, () => prepareImage(element));
      } else if (element instanceof HTMLTemplateElement) {
        const card = element.isConnected && element.matches('[data-ac-product-card-secondary-image]')
          ? element.closest('[data-ac-product-card]') : null;
        if (card) enqueue(card, () => {
          if (!card.isConnected) return;
          const image = window.AcProductCards?.preload(card);
          return image ? prepareImage(image) : undefined;
        });
        else warm(element.content);
      } else enqueue(element, () => {
        if (!element.isConnected) return;
        return waitFor(element, 'canplaythrough', () => window.ACMedia?.preload(element));
      });
    }
  };

  const prefetch = (card) => {
    if (!started || document.hidden || !supportsPrefetch || !card?.isConnected) return;
    const value = card.querySelector('[data-ac-product-card-link]')?.getAttribute('href') || card.dataset.acProductUrl;
    if (!value) return;
    const url = new URL(value, location.href);
    if (url.origin !== location.origin || !/\/products\/[^/]+\/?$/.test(url.pathname)
      || [...url.searchParams.keys()].some((key) => key !== 'variant')
      || url.pathname === location.pathname || rules.has(url.href)) return;
    const rule = document.createElement('script');
    rule.type = 'speculationrules';
    rule.textContent = JSON.stringify({ prefetch: [{ urls: [url.href], eagerness: 'immediate' }] });
    document.head.append(rule);
    rules.set(url.href, rule);
    // A bounded set of likely destinations; the browser owns response freshness.
    if (rules.size > 6) {
      const oldest = rules.keys().next().value;
      rules.get(oldest).remove();
      rules.delete(oldest);
    }
  };
  const cancelDwell = (card) => {
    clearTimeout(dwellTimers.get(card));
    dwellTimers.delete(card);
  };
  const observer = supportsPrefetch && 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      for (const entry of entries) {
        cancelDwell(entry.target);
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5 && !document.hidden) {
          dwellTimers.set(entry.target, setTimeout(() => {
            dwellTimers.delete(entry.target);
            prefetch(entry.target);
          }, 700));
        }
      }
    }, { threshold: [0, 0.5] }) : null;

  const scan = (scope = document) => {
    if (!started) return;
    if (observer) for (const card of within(scope, '[data-ac-product-card][data-ac-product-url]')) {
      if (observed.has(card)) continue;
      observed.add(card);
      observer.observe(card);
    }
    if (home) {
      if (scope === document && document.querySelector('main')) warm(document.querySelector('main'));
      warm(scope);
    }
    pump();
  };
  const forget = (scope) => {
    for (const card of within(scope, '[data-ac-product-card]')) {
      cancelDwell(card);
      observer?.unobserve(card);
      observed.delete(card);
    }
  };
  const cardFor = (event) => event.target.closest?.('[data-ac-product-card]');
  if (supportsPrefetch) {
    on(document, 'pointerover', (event) => {
      const card = cardFor(event);
      if (!card || card.contains(event.relatedTarget) || event.pointerType === 'touch') return;
      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(() => prefetch(card), 150);
    }, { passive: true });
    on(document, 'pointerout', (event) => {
      const card = cardFor(event);
      if (card && !card.contains(event.relatedTarget)) clearTimeout(hoverTimer);
    }, { passive: true });
    on(document, 'pointerdown', (event) => prefetch(cardFor(event)), { passive: true });
    on(document, 'focusin', (event) => prefetch(cardFor(event)));
  }

  const start = () => {
    if (started || document.hidden) return;
    started = true;
    scan();
  };
  const schedule = () => {
    clearTimeout(startTimer);
    startTimer = setTimeout(start, 1200);
  };
  if (document.readyState === 'complete') schedule();
  else on(window, 'load', schedule, { once: true });
  on(document, 'visibilitychange', () => {
    if (document.hidden) {
      clearTimeout(hoverTimer);
      for (const card of dwellTimers.keys()) cancelDwell(card);
    } else if (started) {
      // Re-observe to require a fresh visible dwell after returning to this tab.
      for (const card of within(document, '[data-ac-product-card]')) observed.delete(card);
      observer?.disconnect();
      scan();
    } else if (document.readyState === 'complete') schedule();
  });
  on(document, 'shopify:section:load', (event) => scan(event.target));
  on(window, 'pagehide', (event) => {
    clearTimeout(startTimer);
    clearTimeout(hoverTimer);
    for (const card of dwellTimers.keys()) cancelDwell(card);
    if (event.persisted) return;
    events.abort();
    observer?.disconnect();
    jobs.length = 0;
  });
  on(window, 'pageshow', (event) => {
    if (event.persisted) started ? scan() : schedule();
  });
  window.ACPreload = { scan, forget };
})();
